import { Box, IconButton, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import { GeneralTableBox, HeadTableCell, RowTableCell, NoDataSkeleton, CustomButton } from '../../../../../common';
import { useState } from 'react';
import apiClient from '../../../../../hooks/apiClient';
import HoverExpandableAmmounts from '../../../../../coupons/view-coupons/components/HoverExpandableAmmounts';
import { formatDate, formatCurrency } from '../../../../../utils/formatUtils';
import { exportCouponsToExcel } from '../../../utils/exportCouponsExcel';

/**
 * Tab de cupones del contrato
 * Muestra todos los cupones históricos del contrato con opción de descarga
 */
const CouponsTab = ({ localContract }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const getFeeDisplay = (coupon) => {
    if (coupon.fee_number1 === 0) return 'Adecuación';
    if (coupon.is_adjudication) return 'Adjudicación';
    if (coupon.is_score) return 'De Puntaje';
    if (coupon.is_advance_payment) return 'Adelanto';
    if (coupon.fee_number2 && coupon.fee_number2 !== 0) {
      return `${coupon.fee_number1} - ${coupon.fee_number2}`;
    }
    return coupon.fee_number1 || '-';
  };

  const getVencimientos = (coupon) => {
    const feeTypeName = getFeeDisplay(coupon);

    if (feeTypeName === 'Adjudicación' || feeTypeName === 'Adelanto') {
      return [`Venc: ${formatDate(coupon.first_due_date)} ${formatCurrency(coupon.first_due_amount)}`];
    }

    return [
      `1° vencimiento: ${formatDate(coupon.first_due_date)} | ${formatCurrency(coupon.first_due_amount)}`,
      `2° vencimiento: ${formatDate(coupon.second_due_date)} | ${formatCurrency(coupon.second_due_amount)}`,
      `3° vencimiento: ${formatDate(coupon.third_due_date)} | ${formatCurrency(coupon.third_due_amount)}`,
    ];
  };

  const handleDownload = async (couponUuid) => {
    try {
      setDownloadingId(couponUuid);
      const response = await apiClient.get(`coupons/print/${couponUuid}`);

      const base64Data = response.data.base64_pdf;
      const fileName = response.data.file_name;

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el cupón:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExportExcel = async () => {
    if (!coupons || coupons.length === 0) return;
    
    try {
      setIsExportingExcel(true);
      const contractInfo = {
        contract_number: localContract.contract_number,
        subscriber_name: localContract.subscriber_name,
        plan_name: localContract.plan_name,
      };
      
      const filename = `Cupones_${localContract.contract_number || 'Contrato'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      await exportCouponsToExcel(coupons, contractInfo, filename);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const coupons = localContract.coupons || [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Cupones del Contrato
        </Typography>
        {coupons.length > 0 && (
          <CustomButton
            variant="contained"
            startIcon={<TableChartIcon />}
            onClick={handleExportExcel}
            disabled={isExportingExcel}
            size="small"
          >
            {isExportingExcel ? 'Exportando...' : 'Exportar Excel'}
          </CustomButton>
        )}
      </Box>

      <GeneralTableBox>
        <Table aria-label="coupons table">
          <TableHead>
            <TableRow>
              <HeadTableCell>Mes/Año</HeadTableCell>
              <HeadTableCell>Cuota</HeadTableCell>
              <HeadTableCell>Fecha de Pago</HeadTableCell>
              <HeadTableCell>Vencimientos</HeadTableCell>
              <HeadTableCell>Descargar</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.length > 0 ? (
              coupons.map((coupon, index) => (
                <TableRow key={index}>
                  <RowTableCell>
                    {coupon.month && coupon.year ? `${coupon.month}/${coupon.year}` : '-'}
                  </RowTableCell>
                  <RowTableCell>{getFeeDisplay(coupon)}</RowTableCell>
                  <RowTableCell>
                    {coupon.cancellation_date ? (
                      <Typography sx={{ color: 'green', fontWeight: 'medium' }}>
                        {formatDate(coupon.cancellation_date)}
                      </Typography>
                    ) : (
                      <Typography sx={{ color: 'orange', fontWeight: 'medium' }}>
                        No
                      </Typography>
                    )}
                  </RowTableCell>
                  <RowTableCell>
                    <HoverExpandableAmmounts items={getVencimientos(coupon)} />
                  </RowTableCell>
                  <RowTableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleDownload(coupon.uuid)}
                      disabled={downloadingId === coupon.uuid}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </RowTableCell>
                </TableRow>
              ))
            ) : (
              <NoDataSkeleton colSpan={5} message="No hay cupones para este contrato" />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
    </Box>
  );
};

export default CouponsTab;
